import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { FiUserPlus, FiRefreshCw, FiSearch, FiCheck, FiLoader } from 'react-icons/fi';
import DeleteConfirmModal from '../../../common/DeleteConfirmModal';
import PermissionGuard from '../../../common/PermissionGuard';
import { coursesApi } from '../../../../services/api/courses.api';
import { toastService } from '../../../../services/toast';
import axios from '../../../../config/axios';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const RESET_DELAY = 300;

export default function EnrollmentsPage() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [addCourseId, setAddCourseId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addedStudents, setAddedStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const resetTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await coursesApi.getAll({ per_page: 100 });
        setCourses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load courses', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Server-side search with debounce
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    setSelectedStudentId(null);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await axios.get('/api/admin/users', {
          params: { search: value.trim(), per_page: 20 }
        });
        setSearchResults(res.data?.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const handleAddSubmit = useCallback(async () => {
    if (!addCourseId || !selectedStudentId) return;
    const course = courses.find(c => c.id === Number(addCourseId));
    const student = searchResults.find(s => s.id === selectedStudentId);
    if (!course || !student) return;
    try {
      await coursesApi.enrollStudent(addCourseId, selectedStudentId);
      setAddedStudents(prev => [{ course: course.name, student: student.name, date: new Date().toLocaleDateString('en-GB') }, ...prev]);
      setSelectedStudentId(null);
      setSearchQuery('');
      setSearchResults([]);
      toastService.success(`تمت إضافة ${student.name} كمشترك مجاني بنجاح`);
    } catch (err) {
      const msg = err.response?.data?.message || 'فشلت إضافة المشترك';
      toastService.error(msg);
    }
  }, [addCourseId, selectedStudentId, courses, searchResults]);

  const handleResetClick = useCallback((course) => {
    if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    setSelectedCourse(course);
    setResetDialogOpen(true);
  }, []);

  const handleResetConfirm = useCallback(async () => {
    if (!selectedCourse) return;
    try {
      const res = await coursesApi.resetEnrollments(selectedCourse.id);
      setCourses(prev => prev.map(c => c.id === selectedCourse.id ? { ...c, students_count: 0 } : c));
      toastService.success(res.message || 'تم تصفير الدورة بالكامل');
    } catch (err) {
      toastService.error('فشل تصفير التسجيلات');
    }
    setResetDialogOpen(false);
    resetTimeoutRef.current = setTimeout(() => setSelectedCourse(null), RESET_DELAY);
  }, [selectedCourse]);

  const closeResetDialog = useCallback(() => {
    setResetDialogOpen(false);
    resetTimeoutRef.current = setTimeout(() => setSelectedCourse(null), RESET_DELAY);
  }, []);

  const tabs = [
    {
      name: 'إضافة مشترك مجاني',
      icon: FiUserPlus,
      content: (
        <div className="p-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">الدورة</label>
              <select
                value={addCourseId}
                onChange={e => setAddCourseId(e.target.value)}
                className="input-base h-[38px] py-1 w-full"
              >
                <option value="">-- اختر الدورة --</option>
                {courses.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">البحث عن متدرب</label>
              <div className="relative">
                <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => handleSearchChange(e.target.value)}
                    placeholder="ابحث بالاسم أو البريد أو الجوال..."
                    className="input-base pr-10"
                  />
                  {searching && (
                    <FiLoader className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                  )}
              </div>
            </div>
          </div>

          {searchQuery && !searching && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="max-h-48 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
                {searchResults.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-400">لا توجد نتائج</div>
                ) : searchResults.map(s => (
                  <button
                    key={s.id}
                    onClick={() => { setSelectedStudentId(s.id); setSearchQuery(s.name); }}
                    className={`w-full flex items-center gap-3 p-3 text-right transition-colors ${
                      selectedStudentId === s.id
                        ? 'bg-primary-50 dark:bg-gray-700'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      selectedStudentId === s.id
                        ? 'bg-primary-600 text-white dark:bg-primary-500 dark:text-white'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}>
                      {s.name?.[0] || '?'}
                    </div>
                    <div className="flex-1 text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{s.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{s.email} | {s.phone}</div>
                    </div>
                    {selectedStudentId === s.id && (
                      <FiCheck className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleAddSubmit}
              disabled={!addCourseId || !selectedStudentId}
              className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              <FiUserPlus className="w-4 h-4" />
              إضافة مشترك مجاني
            </button>
          </div>

          {addedStudents.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">آخر الإضافات</h3>
              <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">المتدرب</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">الدورة</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">التاريخ</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {addedStudents.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.student}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{item.course}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">{item.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      name: 'تصفير التسجيلات',
      icon: FiRefreshCw,
      content: (
        <div className="overflow-hidden">
          <div className="mx-4 mt-4 mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            <p className="font-semibold mb-1">⚠️ تنبيه مهم</p>
            <p>هذا الإجراء يقوم بمسح جميع المشتركين والمبيعات المرتبطة بالدورة بالكامل. لا يمكن التراجع عنه.</p>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">الدورة</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">المدرب</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">المسجلين</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">الحالة</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">الإجراء</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {courses.map((course) => (
                    <tr key={course.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{course.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {course.instructors?.map(i => i.name).join('، ') || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">{course.students_count ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          course.status === 'ended'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            : course.status === 'published'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {course.status === 'ended' ? 'منتهية' : course.status === 'published' ? 'منشورة' : 'مسودة'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleResetClick(course)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                        >
                          <FiRefreshCw className="w-4 h-4" />
                          تصفير
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <PermissionGuard permission="view_financial_statistics">
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">إدارة المشتركين</h2>
          </div>
        </div>
        <div className="p-6">
          <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
            <Tab.List className="flex gap-2 rounded-xl bg-gray-100 dark:bg-gray-700 p-1 mb-0">
              {tabs.map((tab, idx) => (
                <Tab
                  key={idx}
                  className={({ selected }) =>
                    classNames(
                      'w-full rounded-lg py-3 text-sm font-medium leading-5',
                      'focus:outline-none',
                      'flex items-center justify-center gap-2',
                      selected
                        ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-white/[0.12] dark:hover:bg-gray-500/50'
                    )
                  }
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.name}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              {tabs.map((tab, idx) => (
                <Tab.Panel key={idx} as="div">
                  {tab.content}
                </Tab.Panel>
              ))}
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={resetDialogOpen}
        onClose={closeResetDialog}
        onConfirm={handleResetConfirm}
        itemType="الدورة"
        itemTitle={selectedCourse?.name}
        customMessage="هل أنت متأكد من تصفير جميع المشتركين من الدورة"
      />
    </div>
    </PermissionGuard>
  );
}
